import { Flight, FlightStatus } from "@/types/flight";
import { formatTime } from "@/lib/formatters";
import { X, Copy, Facebook, MessageCircle, Share2 } from "lucide-react";
import { useEffect, useRef, useState, useCallback } from "react";

interface FlightDetailPanelProps {
  flight: Flight | null;
  onClose: () => void;
}

function getStatusStyle(status: FlightStatus): { bg: string; text: string } {
  switch (status) {
    case "Delayed":
      return { bg: "#F59E0B", text: "#000000" };
    case "Cancelled":
      return { bg: "#FF4444", text: "#FFFFFF" };
    case "Landed":
    case "Arrived":
      return { bg: "#3B82F6", text: "#FFFFFF" };
    case "Boarding":
    case "Boarding Soon":
    case "Departed":
    case "In Air":
      return { bg: "#44FF88", text: "#000000" };
    case "On Time":
    case "Scheduled":
      return { bg: "#22C55E", text: "#000000" };
    case "Gate Closed":
      return { bg: "#991B1B", text: "#FFFFFF" };
    case "Check-in Open":
      return { bg: "#2563EB", text: "#FFFFFF" };
    default:
      return { bg: "#6B7280", text: "#FFFFFF" };
  }
}

function XTwitterIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

export function FlightDetailPanel({ flight, onClose }: FlightDetailPanelProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (flight) {
      requestAnimationFrame(() => setIsVisible(true));
    } else {
      setIsVisible(false);
    }
  }, [flight]);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  const handleBackdropClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  }, [onClose]);

  if (!flight) return null;

  const codeshares = flight.flight_number.includes("/")
    ? flight.flight_number.split("/")
    : [flight.flight_number];

  const stdTime = formatTime(flight.scheduled_departure);
  const etdTime = flight.etd ? formatTime(flight.etd) : stdTime;
  const etdDiffers = etdTime !== stdTime;

  const etaTime = (() => {
    if (flight.eta) return formatTime(flight.eta);
    if (flight.scheduled_arrival && flight.scheduled_arrival !== flight.scheduled_departure) {
      return formatTime(flight.scheduled_arrival);
    }
    return null;
  })();

  const statusStyle = getStatusStyle(flight.status);

  const shareUrl = `https://pngflightradar.com/flight-board?flight=${flight.flight_number}`;
  const shareMessageLines = [
    `✈️ Flight Update — ${flight.flight_number}`,
    flight.organization?.name || "Airline",
    "",
    `🛫 From: ${flight.departure_city?.name || "?"} (${flight.departure_city?.code || "?"})`,
    `🛬 To: ${flight.arrival_city?.name || "?"} (${flight.arrival_city?.code || "?"})`,
    "",
    `🕐 ETD: ${etdTime}`,
    `🕐 ETA: ${etaTime || "TBA"}`,
    `🚪 Gate: ${flight.gate || "TBA"}`,
    `📋 Status: ${flight.status}`,
  ];
  if (flight.aircraft_type) {
    shareMessageLines.push(`Aircraft: ${flight.aircraft_type.name} (${flight.aircraft_type.code})`);
  }
  if (flight.status_reason) {
    shareMessageLines.push(`Notes: ${flight.status_reason}`);
  }
  shareMessageLines.push("", `🔗 Track this flight live:`, shareUrl, "", "Powered by PNG Flight Radar | pngflightradar.com");
  const shareMessage = shareMessageLines.join("\n");
  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedMessage = encodeURIComponent(shareMessage);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(`${shareMessage}\n${shareUrl}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback
    }
  };

  const handleNativeShare = () => {
    if (navigator.share) {
      navigator.share({ title: `Flight ${flight.flight_number}`, text: shareMessage, url: shareUrl });
    }
  };

  const canNativeShare = typeof navigator !== "undefined" && !!navigator.share;

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end"
      onClick={handleBackdropClick}
      style={{ backgroundColor: isVisible ? "rgba(0,0,0,0.5)" : "rgba(0,0,0,0)" , transition: "background-color 0.3s ease" }}
    >
      <div
        ref={panelRef}
        className="h-full overflow-y-auto w-full sm:w-[400px] flex flex-col"
        style={{
          backgroundColor: "#0a0f2c",
          borderLeft: "3px solid #00c8d7",
          transform: isVisible ? "translateX(0)" : "translateX(100%)",
          transition: "transform 0.3s ease-out",
        }}
      >
        {/* Header */}
        <div className="flex items-start justify-between p-5" style={{ borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
          <div className="flex-1 min-w-0">
            <div className="text-white/50 text-sm mb-1">{flight.organization?.name || "Airline"}</div>
            <div className="flex items-baseline gap-2 flex-wrap">
              {codeshares.map((cs, i) => (
                <span key={i} className={`font-bold font-mono ${i === 0 ? "text-2xl text-white" : "text-lg text-white/60"}`}>
                  {cs.trim()}
                </span>
              ))}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/10 transition-colors ml-2 shrink-0"
            aria-label="Close panel"
          >
            <X className="h-5 w-5 text-white/70" />
          </button>
        </div>

        {/* Route */}
        <div className="px-5 py-4" style={{ borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
          <div className="text-white/50 text-xs uppercase tracking-wider mb-2">Route</div>
          <div className="flex items-center gap-3">
            <div className="text-center">
              <div className="text-white font-bold text-lg">{flight.departure_city?.code || "?"}</div>
              <div className="text-white/60 text-xs">{flight.departure_city?.name}</div>
            </div>
            <div className="flex-1 flex items-center justify-center">
              <div className="h-px flex-1" style={{ backgroundColor: "rgba(255,255,255,0.2)" }} />
              <span className="px-2 text-white/40 text-lg">→</span>
              <div className="h-px flex-1" style={{ backgroundColor: "rgba(255,255,255,0.2)" }} />
            </div>
            <div className="text-center">
              <div className="text-white font-bold text-lg">{flight.arrival_city?.code || "?"}</div>
              <div className="text-white/60 text-xs">{flight.arrival_city?.name}</div>
            </div>
          </div>
        </div>

        {/* Times */}
        <div className="px-5 py-4" style={{ borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
          <div className="text-white/50 text-xs uppercase tracking-wider mb-3">Times</div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-white/50 text-xs mb-1">ETD</div>
              <div className={`font-bold font-mono text-lg ${etdDiffers ? "text-amber-400" : "text-white"}`}>
                {etdTime}
              </div>
            </div>
            <div>
              <div className="text-white/50 text-xs mb-1">ETA</div>
              <div className="text-white font-bold font-mono text-lg">
                {etaTime || <span className="text-white/40">TBA</span>}
              </div>
            </div>
          </div>
        </div>

        {/* Status & Gate */}
        <div className="px-5 py-4" style={{ borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <div className="text-white/50 text-xs mb-1">Status</div>
              <span
                className="inline-block px-3 py-1 rounded-full text-sm font-bold"
                style={{ backgroundColor: statusStyle.bg, color: statusStyle.text }}
              >
                {flight.status}
              </span>
              {flight.status_reason && (
                <div className="text-white/50 text-xs mt-1">{flight.status_reason}</div>
              )}
            </div>
            <div>
              <div className="text-white/50 text-xs mb-1">Gate</div>
              <div className="text-white font-bold text-lg">{flight.gate || "TBA"}</div>
            </div>
          </div>
        </div>

        {/* Aircraft & Details */}
        <div className="px-5 py-4" style={{ borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
          {flight.aircraft_type && (
            <div className="mb-3">
              <div className="text-white/50 text-xs mb-1">Aircraft</div>
              <div className="text-white text-sm">{flight.aircraft_type.name} ({flight.aircraft_type.code})</div>
            </div>
          )}
          {flight.updated_at && (
            <div>
              <div className="text-white/50 text-xs mb-1">Last Updated</div>
              <div className="text-white/60 text-xs">
                {flight.updated_at ? new Date(flight.updated_at).toLocaleString("en-GB", { timeZone: "Pacific/Port_Moresby" }) : "—"}
              </div>
            </div>
          )}
        </div>

        {/* Share */}
        <div className="px-5 py-4 mt-auto">
          <div className="text-white/50 text-xs uppercase tracking-wider mb-3">Share this flight</div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleCopy}
              className="flex flex-col items-center gap-1 p-2 rounded-lg hover:bg-white/10 transition-colors"
            >
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: "rgba(0,200,215,0.15)", border: "1px solid rgba(0,200,215,0.3)" }}>
                <Copy className="h-4 w-4" style={{ color: "#00c8d7" }} />
              </div>
              <span className="text-white/50 text-[10px]">{copied ? "Copied!" : "Copy"}</span>
            </button>

            <a
              href={`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center gap-1 p-2 rounded-lg hover:bg-white/10 transition-colors"
            >
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: "rgba(24,119,242,0.15)", border: "1px solid rgba(24,119,242,0.3)" }}>
                <Facebook className="h-4 w-4 text-[#1877F2]" />
              </div>
              <span className="text-white/50 text-[10px]">Facebook</span>
            </a>

            <a
              href={`https://twitter.com/intent/tweet?text=${encodedMessage}&url=${encodedUrl}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center gap-1 p-2 rounded-lg hover:bg-white/10 transition-colors"
            >
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)" }}>
                <XTwitterIcon className="h-4 w-4 text-white" />
              </div>
              <span className="text-white/50 text-[10px]">X</span>
            </a>

            <a
              href={`https://wa.me/?text=${encodedMessage}%20${encodedUrl}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center gap-1 p-2 rounded-lg hover:bg-white/10 transition-colors"
            >
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: "rgba(37,211,102,0.15)", border: "1px solid rgba(37,211,102,0.3)" }}>
                <MessageCircle className="h-4 w-4 text-[#25D366]" />
              </div>
              <span className="text-white/50 text-[10px]">WhatsApp</span>
            </a>

            {canNativeShare && (
              <button
                onClick={handleNativeShare}
                className="flex flex-col items-center gap-1 p-2 rounded-lg hover:bg-white/10 transition-colors"
              >
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: "rgba(0,200,215,0.15)", border: "1px solid rgba(0,200,215,0.3)" }}>
                  <Share2 className="h-4 w-4" style={{ color: "#00c8d7" }} />
                </div>
                <span className="text-white/50 text-[10px]">Share</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
